import React, { useEffect, useRef, useState } from 'react'
import { graphql, useStaticQuery } from 'gatsby'
import { PrismicRichText } from '@prismicio/react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Heading3, Heading4, IntenseText } from 'components/design'

import 'swiper/css'

export default function TestimonialsSection(props) {
	const { testimonials } = useTestimonialsData()
	const slideCount = testimonials.length
	const sliderRef = useRef()
	const [currentSlide, setCurrentSlide] = useState(0)

	useEffect(() => {
		sliderRef.current.swiper.updateSize()
	})

	const previousImage = () => {
		sliderRef.current.swiper.slidePrev()
	}
	const nextImage = () => {
		sliderRef.current.swiper.slideNext()
	}
	const slideTo = (index) => {
		sliderRef.current.swiper.slideTo(index)
	}

	return (
		<>
			<Swiper
				className={props.className}
				ref={sliderRef}
				centeredSlides={true}
				slidesPerView={'auto'}
				spaceBetween={0}
				onActiveIndexChange={(swiper) => {
					setCurrentSlide(swiper.realIndex)
				}}
			>
				{testimonials.map((testimonial, index) => (
					<SwiperSlide
						key={testimonial.author_name}
						className="!h-auto w-full md:px-[calc(1/2*var(--content-grid-step))] lg:px-[calc(1*var(--content-grid-step))] xl:!w-[calc(30*var(--content-grid-step))] xl:px-[calc(2*var(--content-grid-step))]"
					>
						<div className="h-full w-full">
							<div className="relative h-full bg-black/5 p-5 pb-8 md:py-10 md:px-20 lg:px-32 lg:py-16 xl:py-20 xl:px-40 2xl:px-48 2xl:py-24">
								<div className="ml-auto flex h-20 w-20 items-center justify-items-end pb-8 md:absolute md:top-0 md:right-12 md:p-0 lg:h-32 lg:w-32 2xl:right-16 2xl:top-2">
									<img
										className="w-full"
										src={testimonial.logo.url}
										alt={testimonial.logo.alt}
									/>
								</div>
								<div>
									<Heading3 className="pb-2">
										{testimonial.author_name}
									</Heading3>
									<Heading4>{testimonial.author_position}</Heading4>

									<IntenseText className="pt-4 font-normal text-pale xl:pt-6">
										<PrismicRichText
											field={testimonial.text.richText}
										/>
									</IntenseText>
								</div>
							</div>
						</div>
					</SwiperSlide>
				))}
				{currentSlide === 0 ? null : (
					<button
						title="Previous testimonial"
						onClick={previousImage}
						className="absolute z-10 hidden h-full w-2 w-full md:top-0 md:left-0 md:block md:w-[calc(1/2*var(--content-grid-step))] lg:w-[calc((1.5*var(--content-grid-step))-4rem)] xl:w-[var(--content-grid-step)]"
					>
						<span className="hidden">Previous testimonial</span>
					</button>
				)}

				{currentSlide === slideCount - 1 ? null : (
					<button
						title="Next testimonial"
						onClick={nextImage}
						className="absolute z-10 hidden h-full opacity-0 md:top-0 md:right-0 md:block md:w-[calc(1/2*var(--content-grid-step))] lg:w-[calc((1.5*var(--content-grid-step))-4rem)] xl:w-[var(--content-grid-step)]"
					>
						<span className="hidden">Next testimonial</span>
					</button>
				)}
			</Swiper>
			<div className="flex items-center justify-start p-4 sm:justify-center xl:mt-4">
				{testimonials.map((t, index) => {
					const isCurrent = index === currentSlide

					return (
						// eslint-disable-next-line
						<button
							key={`testimonial-${index}`}
							className={`mr-2 block h-2 last:m-0 ${
								isCurrent
									? 'w-10 cursor-default bg-accent'
									: 'w-2 bg-black'
							} transition-all duration-200`}
							style={{
								opacity: isCurrent
									? 1
									: 1 - index / (testimonials.length - 0.5),
							}}
							onClick={() => !isCurrent && slideTo(index)}
						/>
					)
				})}
			</div>
		</>
	)
}

const useTestimonialsData = () => {
	const data = useStaticQuery(graphql`
		query testimonialsQuery {
			prismicTestimonials {
				_previewable
				data {
					body {
						... on PrismicTestimonialsDataBodyTestimonial {
							id
							primary {
								author_name
								author_position
								logo {
									alt
									url
								}
								text {
									richText
								}
							}
						}
					}
				}
			}
		}
	`)

	return {
		testimonials: data.prismicTestimonials.data.body.map((item) => item.primary),
	}
}
